import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicNav, Footer } from "@/components/PublicChrome";
import { Embers } from "@/components/Embers";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bitcoin, Coins, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ChallengesPage() {
  const [challenges, setChallenges] = useState([]);
  const [tab, setTab] = useState("1-Step");
  const [selected, setSelected] = useState(null);
  const [payCurrency, setPayCurrency] = useState("usdtbsc");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/challenges").then((r) => setChallenges(r.data || []));
  }, []);

  const grouped = {
    "1-Step": challenges.filter((c) => c.challenge_type === "1-Step"),
    "Instant": challenges.filter((c) => c.challenge_type === "Instant"),
  };

  const initiatePurchase = async () => {
    if (!user) {
      toast.info("Sign in to purchase a challenge");
      nav("/login", { state: { from: { pathname: "/challenges" } } });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/nowpayments/create-invoice", {
        challenge_id: selected.id,
        pay_currency: payCurrency,
      });
      if (data.invoice_url) {
        window.location.href = data.invoice_url;
      } else {
        toast.error("Failed to create invoice");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment gateway unavailable");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#f5f5f5]">
      <PublicNav />
      <section className="relative overflow-hidden border-b border-[#ff7a00]/10 py-14">
        <Embers count={16} />
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] bg-[#ff7a00]/15 rounded-full blur-[140px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-[#ff7a00] text-xs tracking-[0.3em] uppercase mb-3">Evaluation Programs</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight">Choose your hunt.</h1>
          <p className="text-[#8a8a8a] mt-3 max-w-xl">Three paths to live capital. Pay in crypto. Get funded fast.</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <Tabs value={tab} onValueChange={setTab} data-testid="challenges-tabs">
            <TabsList className="bg-[#0a0a0a] border border-[#ff7a00]/20 p-1">
              <TabsTrigger value="1-Step" className="data-[state=active]:bg-[#ff7a00] data-[state=active]:text-[#050505] data-[state=active]:font-semibold uppercase text-xs tracking-widest" data-testid="tab-1-step">1-Step</TabsTrigger>
              <TabsTrigger value="Instant" className="data-[state=active]:bg-[#ff7a00] data-[state=active]:text-[#050505] data-[state=active]:font-semibold uppercase text-xs tracking-widest gap-2" data-testid="tab-instant">
                Instant
                <span className="text-[9px] tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#ff7a00]/15 text-[#ff9a2f] border border-[#ff7a00]/30">SOON</span>
              </TabsTrigger>
            </TabsList>
            {Object.entries(grouped).map(([t, items]) => (
              <TabsContent key={t} value={t} className="mt-8">
                {t === "Instant" && (
                  <div className="mb-6 glass rounded-xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-[#ff7a00]/30" data-testid="instant-coming-soon-banner">
                    <div>
                      <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[#ff7a00]/40 bg-[#ff7a00]/10 text-[#ff9a2f] text-[10px] tracking-[0.25em] uppercase">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#ff7a00] animate-pulse" /> Coming Soon
                      </div>
                      <div className="font-display text-lg sm:text-xl font-semibold mt-2">Instant Funding is launching shortly.</div>
                      <p className="text-[#8a8a8a] text-sm mt-1 max-w-xl">We're finalising terms and onboarding. In the meantime, get funded the proven way — start a 1-Step challenge.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setTab("1-Step")}
                      className="self-start sm:self-auto px-5 h-11 rounded-md bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold text-sm transition-colors"
                      data-testid="instant-switch-to-1step"
                    >
                      View 1-Step Plans
                    </button>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {items.map((c, i) => {
                    const isInstant = t === "Instant";
                    return (
                      <motion.div
                        key={c.id}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className={`glass rounded-xl p-6 transition-all relative ${
                          isInstant ? "opacity-60 grayscale-[0.4]" : "hover:border-[#ff7a00]/50 hover:-translate-y-1"
                        }`}
                        data-testid={`challenge-${c.id}`}
                      >
                        {isInstant && (
                          <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full border border-[#ff7a00]/40 bg-[#0a0a0a]/80 text-[#ff9a2f] text-[9px] tracking-[0.25em] uppercase" data-testid={`challenge-coming-soon-${c.id}`}>
                            Coming Soon
                          </div>
                        )}
                        <div className="text-[10px] text-[#8a8a8a] tracking-[0.25em] uppercase">{c.name}</div>
                        <div className="mt-3 font-display text-3xl font-bold">${c.account_size.toLocaleString()}</div>
                        <div className="text-[#ff9a2f] text-sm mt-1">Account Size</div>
                        <div className="mt-5 space-y-2 text-sm text-[#cfcfcf]">
                          <div className="flex justify-between"><span className="text-[#8a8a8a]">Target</span><span>{c.profit_target_pct || "—"}%</span></div>
                          <div className="flex justify-between"><span className="text-[#8a8a8a]">Daily DD</span><span>{c.daily_drawdown_pct}%</span></div>
                          <div className="flex justify-between"><span className="text-[#8a8a8a]">Max DD</span><span>{c.max_drawdown_pct}%</span></div>
                          <div className="flex justify-between"><span className="text-[#8a8a8a]">Leverage</span><span>{c.leverage}</span></div>
                          <div className="flex justify-between"><span className="text-[#8a8a8a]">Split</span><span className="text-[#ff9a2f] font-semibold">{c.profit_split_pct}%</span></div>
                        </div>
                        <div className="mt-6 pt-5 border-t border-[#ff7a00]/15">
                          <div className="flex items-end justify-between mb-4">
                            <div>
                              <div className="text-[10px] text-[#8a8a8a] uppercase tracking-wider">Price</div>
                              {isInstant ? (
                                <div className="font-display text-lg font-bold text-[#ff9a2f] tracking-tight">Coming Soon</div>
                              ) : (
                                <div className="font-display text-2xl font-bold">${c.price_usd}</div>
                              )}
                            </div>
                          </div>
                          <Button
                            disabled={isInstant}
                            className={`w-full font-semibold ${
                              isInstant
                                ? "bg-[#1a1a1a] text-[#8a8a8a] border border-[#ff7a00]/20 cursor-not-allowed hover:bg-[#1a1a1a]"
                                : "bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505]"
                            }`}
                            onClick={() => !isInstant && setSelected(c)}
                            data-testid={`challenge-buy-${c.id}`}
                          >
                            {isInstant ? "Coming Soon" : "Buy Challenge"}
                          </Button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Purchase dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5] max-w-md" data-testid="purchase-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Confirm Purchase</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="glass rounded-lg p-4">
                  <div className="text-[10px] text-[#8a8a8a] uppercase tracking-widest">{selected.challenge_type} · {selected.name}</div>
                  <div className="font-display text-2xl font-bold mt-1">${selected.account_size.toLocaleString()}</div>
                  <div className="text-[#ff9a2f] text-sm mt-1">Target {selected.profit_target_pct || 0}% · Max DD {selected.max_drawdown_pct}% · {selected.profit_split_pct}% split</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#cfcfcf] mb-2">Pay With</div>
                  <Select value={payCurrency} onValueChange={setPayCurrency}>
                    <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 h-11" data-testid="pay-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]">
                      <SelectItem value="usdtbsc">USDT (BEP20) — Recommended</SelectItem>
                      <SelectItem value="usdttrc20">USDT (TRC20)</SelectItem>
                      <SelectItem value="usdterc20">USDT (ERC20)</SelectItem>
                      <SelectItem value="usdc">USDC</SelectItem>
                      <SelectItem value="btc">Bitcoin (BTC)</SelectItem>
                      <SelectItem value="eth">Ethereum (ETH)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-[#ff7a00]/15">
                  <span className="text-[#8a8a8a] text-sm">Total</span>
                  <span className="font-display text-2xl font-bold text-[#ff7a00]">${selected.price_usd}</span>
                </div>
                <Button onClick={initiatePurchase} disabled={busy} className="w-full h-12 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold glow-orange-soft" data-testid="confirm-purchase">
                  {busy ? "Creating Invoice..." : "Proceed to Payment"}
                </Button>
                <div className="text-[10px] text-[#8a8a8a] flex items-center gap-2 justify-center">
                  <ShieldCheck size={12} /> Secured via NOWPayments
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
